var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { loadingState } from '@/components/LoadingView';
import useAppComponents from '@/hooks/useAppComponents';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
function findDeletableItem(items, fallback) {
    const deletableItems = items === null || items === void 0 ? void 0 : items.filter(a => a.isDeletable);
    if (deletableItems && deletableItems.length > 0) {
        return deletableItems.sort((a, b) => b.createdAt.value - a.createdAt.value)[0];
    }
    return (fallback === null || fallback === void 0 ? void 0 : fallback.isDeletable) ? fallback : undefined;
}
export function useDeleteAccessibility(type, placeId, accessibilityDto) {
    const { api } = useAppComponents();
    const [loading, setLoading] = useAtom(loadingState);
    const queryClient = useQueryClient();
    const isDeletable = type === 'place'
        ? !!findDeletableItem(accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.placeAccessibilities, accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.placeAccessibility)
        : !!findDeletableItem(accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.buildingAccessibilities, accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.buildingAccessibility);
    const mutation = useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            if (type === 'place') {
                const target = findDeletableItem(accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.placeAccessibilities, accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.placeAccessibility);
                if (!target) {
                    throw new Error('No deletable placeAccessibility found');
                }
                return yield api.deletePlaceAccessibilityPost({
                    placeAccessibilityId: target.id,
                });
            }
            else {
                const target = findDeletableItem(accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.buildingAccessibilities, accessibilityDto === null || accessibilityDto === void 0 ? void 0 : accessibilityDto.buildingAccessibility);
                if (!target) {
                    throw new Error('No deletable buildingAccessibility found');
                }
                return yield api.deleteBuildingAccessibilityPost({
                    buildingAccessibilityId: target.id,
                });
            }
        }),
        onMutate: () => setLoading(new Map(loading).set('PlaceDetailV2', true)),
        onSuccess: (_data, _variables) => {
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId],
            });
            // Asynchronously update search cache with full latest data
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
            // 정복한 장소 > 내가 정복한 장소 통계
            queryClient.invalidateQueries({
                queryKey: ['ConqueredPlacesForNumberOfItems'],
            });
            // 정복한 장소 > 도움이 돼요 통계
            queryClient.invalidateQueries({
                queryKey: ['UpvotedForNumberOfItems'],
            });
            // 정복한 장소 > 정복 리포트 통계
            queryClient.invalidateQueries({
                queryKey: ['ConquererActivity'],
            });
            // 정복한 장소 > 내가 정복한 장소 리스트
            queryClient.invalidateQueries({
                queryKey: ['ConqueredPlaces'],
            });
            //  정복한 장소 > 도움이 돼요 리스트
            queryClient.invalidateQueries({
                queryKey: ['PlacesUpvoted'],
            });
            if (type === 'place') {
                ToastUtils.show('장소 정보를 삭제했습니다.');
            }
            else {
                ToastUtils.show('건물 정보를 삭제했습니다.');
            }
        },
        onError: (_error, _variables) => {
            if (type === 'place') {
                ToastUtils.show('삭제할 수 없는 장소입니다.');
            }
            else {
                ToastUtils.show('삭제할 수 없는 건물입니다.');
            }
        },
        onSettled: () => {
            setLoading(new Map(loading).set('PlaceDetailV2', false));
        },
    });
    return Object.assign({ isDeletable }, mutation);
}
